import 'leaflet/dist/leaflet.css';
import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { greenCarIcon, blueCarIcon, departureMakerIcon, destinationMarkerIcon } from './icons';
import webSocket from '../../webSocket';
import { subscribeToAllDriverLocation, subscribeToAllDriverStatus, subscribeToAllOrder } from './messageBus';
import { getOrderList } from "../../api/order";
import { getDriverList } from '../../api/driver';
import { Zoom, toast } from 'react-toastify';

// Helper to center map on user's location
const FocusLocation = ({ position }) => {
    const map = useMap();

    useEffect(() => {
        if (position) {
            map.setView(position, 13);
        }
    }, [position, map]);

    return null;
};

const MapBox = () => {
    const defaultCenter = [3.156801, 101.711397]; // Kuala Lumpur coordinates
    const [driverList, setDriverList] = useState([]);
    const [driverDetailsList, setDriverDetailsList] = useState([]);
    const [socketReady, setSocketReady] = useState(false);
    const [focusPosition, setFocusPosition] = useState(null);
    const [orderData, setOrderData] = useState([]);
    const [selectedDriverKey, setSelectedDriverKey] = useState(null);
    const [departurePosition, setDeparturePosition] = useState(null);
    const [destinationPosition, setDestinationPosition] = useState(null);

    useEffect(() => {
        repositionToUserLocation();
        getAllOrder();
        getAllDriverDetails();

        // Subscribe to driver location updates
        const unsubscribeDriverLocation = subscribeToAllDriverLocation((data) => {
            const newData = (data ?? []).filter(driver => driver.status !== 'offline');
            setDriverList(newData);
        });

        // Subscribe to driver status updates
        const unsubscribeDriverStatus = subscribeToAllDriverStatus((data) => {
            setDriverList((prevList) => {
                return prevList.map(driver => {
                    if (driver.key === data.key) {
                        driver.status = data.status;
                    }
                    return driver;
                });
            });
        });

        const unsubscribeOrder = subscribeToAllOrder((data) => {
            if (data.id && !orderData.some(order => order.id === data.id)) {
                setOrderData(prevOrders => [...prevOrders, data]);
                orderNotification(data);
            }
            else if (data.id) {
                setOrderData(prevOrders => prevOrders.map(order => order.id === data.id ? data : order));
            }
        });

        // Connect to WebSocket
        const socketUrl = process.env.REACT_APP_API_URL.replaceAll('http', 'ws').replaceAll('https', 'wss');
        webSocket.connect(socketUrl).then((succeed) => {
            setSocketReady(succeed);
            webSocket.send('broadcastAllDriverLocation', {
                latitude: defaultCenter[0],
                longitude: defaultCenter[1],
                radius: 1000,
                status: ['available', 'on trip'],
            });
        });

        return () => {
            unsubscribeOrder();
            unsubscribeDriverStatus();
            unsubscribeDriverLocation();
        }
    }, []);

    async function getAllDriverDetails() {
        const response = await getDriverList({
            skip: 0,
            limit: 99999,
        });
        setDriverDetailsList(response.items);
    }

    async function getAllOrder() {
        const response = await getOrderList({
            skip: 0,
            limit: 99999,
            status: ['in progress', 'on the way', 'picking up'],
        });

        response.items.sort((a, b) => {
            return new Date(b.createdAt) - new Date(a.createdAt);
        });

        response.items = response.items.reduce((acc, curr) => {
            if (!acc.some(item => item.driverDetails.id === curr.driverDetails.id)) {
                acc.push(curr);
            }
            return acc;
        }, []);

        setOrderData(response.items);
    }

    function repositionToUserLocation() {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setFocusPosition([pos.coords.latitude, pos.coords.longitude]);
            },
            (err) => {
                console.error("Geolocation error:", err);
            }
        );
    }

    function getPopupContent(driver) {
        const driverId = driver.key.split('-').pop();
        const order = orderData.find(o => o.driverDetails.id === driverId);
        
        if (order) {
            const fromDateTime = order?.locationDetails?.fromDateTime;
            const toDateTime = order?.locationDetails?.toDateTime;

            let dateTimeDiff = null;
            let eta = null;
            if (fromDateTime && toDateTime) {
                // Calculate total time difference in hh:mm format
                const diffMs = new Date(toDateTime) - new Date(fromDateTime);
                const diffMins = Math.floor(diffMs / 60000);
                const hours = Math.floor(diffMins / 60);
                const minutes = diffMins % 60;
                dateTimeDiff = `${hours}h ${minutes}m`;

                const etaMs = new Date(toDateTime) - Date.now();
                if (etaMs > 0) {
                    const etaMins = Math.floor(etaMs / 60000);
                    const etaHours = Math.floor(etaMins / 60);
                    const etaMinutes = etaMins % 60;
                    eta = `${etaHours}h ${etaMinutes}m`;
                } else {
                    eta = "Arrived";
                }
            }

            return (
                <div>
                    <strong>{order?.driverDetails?.fullName}</strong><br />
                    Duration: {dateTimeDiff} (ETA: {eta})<br />
                    Status: {driver.status}<br />
                    {order ? <a target='blank' href={'/order/' + order.id}>order details</a> : <></>}
                </div>
            );
        } else {
            const driverDetail = driverDetailsList.find(d => d.userId === driverId);

            return (
                <div>
                    Full Name: {driverDetail?.fullName}<br />
                    Phone: {driverDetail?.countryCode}{driverDetail?.phoneNumber}<br />
                    Status: {driver.status}<br />
                </div>
            );
        }
    }

    function orderNotification(order) {
        toast((<div>
            <strong>New Order Received</strong>
            <div>Type: {order?.driverType}</div>
            <div>Amount: RM {order?.fareDetails?.total || order?.fareDetails} </div>
            <div>Distance: {order?.locationDetails?.distance} km</div>
            <a target='blank' href={'/order/' + order.id}>order details</a>
        </div>), {
            position: "bottom-right",
            autoClose: 20000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Zoom,
        });
    }

    return (
        <MapContainer
            center={defaultCenter} // fallback to Kuala Lumpur coordinates
            zoom={13}
            style={{ height: 'calc(100vh - 57px)', width: '100%' }}
        >
            <TileLayer
                attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {socketReady && (selectedDriverKey ? driverList.filter(driver => driver.key === selectedDriverKey) : driverList).map((driver, index) => (
                <Marker
                    key={driver.key}
                    position={[driver.latitude, driver.longitude]}
                    icon={driver.status === 'available' ? greenCarIcon : blueCarIcon}
                    eventHandlers={{
                        popupclose: () => {
                            setSelectedDriverKey(null);
                            setDeparturePosition(null);
                            setDestinationPosition(null);
                        },
                        click: () => {
                            setSelectedDriverKey(driver.key);

                            const order = orderData.find(o => o.driverDetails.id === driver.key.split('-').pop());
                            if (order?.locationDetails?.desLong && order?.locationDetails?.desLat) {
                                const driverPos = [driver.latitude, driver.longitude];
                                const depPos = [order.locationDetails.depLat, order.locationDetails.depLong];
                                const destPos = [order.locationDetails.desLat, order.locationDetails.desLong];

                                setDeparturePosition(depPos);
                                setDestinationPosition(destPos);

                                const midLat = (driverPos[0] + destPos[0]) / 2;
                                const midLng = (driverPos[1] + destPos[1]) / 2;

                                // Center map between driver and destination
                                setTimeout(() => {
                                    setFocusPosition([midLat, midLng]);
                                }, 100);
                            }
                        },
                    }}
                >
                    <Popup>
                        {getPopupContent(driver)}
                    </Popup>
                </Marker>
            ))}

            {destinationPosition && (
                <Marker
                    position={destinationPosition}
                    icon={destinationMarkerIcon}
                >
                </Marker>
            )}

            {departurePosition && (
                <Marker
                    position={departurePosition}
                    icon={departureMakerIcon}
                >
                </Marker>
            )}

            {/* <Marker position={[3.156801, 101.711397]} icon={greenCarIcon} data-driver-id={'5656565'}>
                <Popup>Status</Popup>
            </Marker> */}
            <FocusLocation position={focusPosition} />
        </MapContainer>
    );
};

export default MapBox;